import styled from 'styled-components';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { APIEndPoints, Request } from '../../apis';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, useParams } from 'react-router-dom';
import {
  ButtonContainer,
  InputWrapper,
  Modal,
  PrimaryButton,
  ProcessedMessage,
  SecondaryButton,
  SectionHeading,
  SectionPreTitle,
  SectionTitle,
  SectionWrapper
} from '../atoms';

type TEditSubCourseModal = {
  isModalOpen: boolean;
  handleModalClose: () => void;
  polId: string;
  details: {
    id: string;
    name: string;
    cost?: number;
  };
  setTriggerUpdate: () => void;
};

type TFormFields = {
  subCourseName: string;
  subCourseAmount?: number;
};

type TOptions = {
  name?: string;
  cost?: number | 'DELETE_FIELD';
};

const editFormSchema = z.object({
  subCourseName: z
    .string()
    .max(100, 'Sub Course Name must be less than 100 characters'),
  subCourseAmount: z.preprocess((val) => Number(val), z.number()).optional()
});

type EditFormSchema = z.infer<typeof editFormSchema>;

const FormFooter = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 45px;
`;

const EditSubCourseModal = ({
  isModalOpen,
  handleModalClose,
  polId,
  details,
  setTriggerUpdate
}: TEditSubCourseModal) => {
  const [successfullyProcessed, setSuccessfullyProcessed] = useState<
    null | boolean
  >(null);
  // const [triggerUpdate, setTriggerUpdate] = useState(false);

  const { merchantId } = useParams();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<EditFormSchema>({
    resolver: zodResolver(editFormSchema),
    defaultValues: {
      subCourseName: details.name,
      subCourseAmount: details.cost
    }
  });

  const editCourseSchema = z.object({
    id: z.string(),
    status: z.number(),
    message: z.string()
  });

  const onSubmit: SubmitHandler<EditFormSchema> = async (data: TFormFields) => {
    const options: TOptions = {};
    if (data.subCourseName) {
      options.name = data.subCourseName;
    }
    if (data.subCourseAmount) {
      options.cost = Number(data.subCourseAmount);
    } else {
      options.cost = 'DELETE_FIELD';
    }

    try {
      const editSubCourse = await Request.patch(
        `${APIEndPoints.courses}/${merchantId}/${polId}/subCategory/${details.id}?access_token`,
        editCourseSchema,
        {
          ...options
        }
      );

      setSuccessfullyProcessed(true);
    } catch {
      setSuccessfullyProcessed(false);
    }
  };

  const handleReset = () => {
    reset();
    handleModalClose();
    if (successfullyProcessed === true) {
      setTriggerUpdate();
    }
    setSuccessfullyProcessed(null);
  };

  return (
    <Modal
      handleClose={handleReset}
      isModalVisible={isModalOpen}
      content={
        <>
          <SectionPreTitle>Sub Course</SectionPreTitle>
          <SectionTitle>Edit {details.name}</SectionTitle>
          {successfullyProcessed === null && (
            <Form onSubmit={handleSubmit(onSubmit)}>
              <SectionWrapper>
                <SectionHeading>Course Details</SectionHeading>
                <InputWrapper
                  name="subCourseName"
                  type="text"
                  label="Sub Course Name"
                  maxLength={100}
                  required
                  register={register}
                  errorMessage={errors.subCourseName?.message}
                />
                <InputWrapper
                  name="subCourseAmount"
                  type="currency"
                  label="Sub Course Amount"
                  register={register}
                  errorMessage={errors.subCourseAmount?.message}
                />
                <FormFooter
                  horizontalSpacing="10px"
                  verticalSpacing="0px"
                  justify="end"
                >
                  <SecondaryButton
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReset();
                    }}
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton type="submit">Edit Sub Course</PrimaryButton>
                </FormFooter>
              </SectionWrapper>
            </Form>
          )}
          {successfullyProcessed === true && (
            <ProcessedMessage heading="Success">
              <p>Sub Course was successfully made.</p>
            </ProcessedMessage>
          )}
          {successfullyProcessed === false && (
            <ProcessedMessage error={true} heading="Error">
              <p>Something went wrong when creating Sub Course</p>
            </ProcessedMessage>
          )}
        </>
      }
    />
  );
};

export { EditSubCourseModal };
